import { useMutation, useQueryClient, useQuery } from "react-query";
import { useNotify } from "react-admin";
import gql from "graphql-tag";
import { graphqlFetch } from "../../../../lib/graphqlClient";

// Hook for fetching reward codes data (for MaterialTable)
export const useRewardCodesQuery = (
  queryParams: {
    where?: any;
    limit?: number;
    offset?: number;
    orderBy?: any[];
  } = {},
  options: any = {}
) => {
  return useQuery(
    ["rewardCodes", queryParams],
    () =>
      graphqlFetch({
        query:
          gql`
            query getRewardCodes(
              $where: reward_codes_bool_exp
              $limit: Int
              $offset: Int
              $orderBy: [reward_codes_order_by!]
            ) {
              reward_codes(
                where: $where
                limit: $limit
                offset: $offset
                order_by: $orderBy
              ) {
                id
                code
                label_name
                status
                reward_catalog_id
                created_at
                updated_at
              }
              reward_codes_aggregate(where: $where) {
                aggregate {
                  count
                }
              }
            }
          `.loc?.source?.body || "",
        variables: queryParams,
      }),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      // Allow caller to disable execution via enabled flag or override defaults
      ...options,
    }
  );
};

// Custom hook for reward mutations
export const useRewardMutation = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const exportRewardCode = useMutation(
    (variables: any) =>
      graphqlFetch({
        query:
          gql`
            mutation exportCodes($input: ReportInput!) {
              report(input: $input) {
                error
                status
              }
            }
          `.loc?.source?.body || "",
        variables,
      }),
    {
      onSuccess: (data) => {
        const status = data?.report?.status;
        const error = data?.report?.error;
        if (status) {
          notify("Reward code exported successfully", { type: "success" });
        } else {
          notify(`Error exporting reward code: ${error || "Unknown error"}`, {
            type: "error",
          });
        }
      },
      onError: (error: Error) => {
        notify(`Error exporting reward code: ${error.message}`, {
          type: "error",
        });
      },
    }
  );

  const generateRewardCode = useMutation(
    (variables: any) =>
      graphqlFetch({
        query:
          gql`
            mutation GenerateRewardCode($input: GenerateRewardCodeInput!) {
              generateRewardCode(input: $input) {
                codes
                error
              }
            }
          `.loc?.source?.body || "",
        variables,
      }).then((data: any) => {
        // Check if the mutation returned an error in the response
        if (data?.generateRewardCode?.error) {
          throw new Error(data.generateRewardCode.error);
        }
        return data;
      }),
    {
      onSuccess: (data) => {
        // Invalidate and refetch relevant queries
        queryClient.invalidateQueries(["rewards"]);
        queryClient.invalidateQueries(["rewardCatalogues"]);
        queryClient.invalidateQueries(["rewardCodes"]);

        notify("ra.notification.created", { type: "success" });
      },
      onError: (error: Error) => {
        notify(`Error creating reward: ${error.message}`, { type: "error" });
      },
    }
  );

  const fetchRewardCodesQuery = useMutation(
    (variables: any) =>
      graphqlFetch({
        query:
          gql`
            query getRewardCodes(
              $where: reward_codes_bool_exp
              $limit: Int
              $offset: Int
              $orderBy: [reward_codes_order_by!]
            ) {
              reward_codes(
                where: $where
                limit: $limit
                offset: $offset
                order_by: $orderBy
              ) {
                id
                code
                label_name
                status
                created_at
              }
              reward_codes_aggregate(where: $where) {
                aggregate {
                  count
                }
              }
            }
          `.loc?.source?.body || "",
        variables,
      }),
    {
      onSuccess: (data) => {
        // No need to invalidate queries for a fetch operation
        console.log("Reward codes fetched successfully:", data);
      },
      onError: (error: Error) => {
        notify(`Error fetching reward codes: ${error.message}`, {
          type: "error",
        });
      },
    }
  );

  const updateRewardMutation = useMutation(
    ({ id, updateData }: { id: string; updateData: any }) =>
      graphqlFetch({
        query:
          gql`
            mutation updateRewardCode(
              $_set: reward_codes_set_input
              $pk_columns: reward_codes_pk_columns_input!
            ) {
              update_reward_codes_by_pk(_set: $_set, pk_columns: $pk_columns) {
                id
                code
                label_name
                status
                reward_catalog_id
                created_at
                updated_at
              }
            }
          `.loc?.source?.body || "",
        variables: {
          _set: updateData,
          pk_columns: { id: id },
        },
      }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["rewards"]);
        queryClient.invalidateQueries(["rewardCatalogues"]);
        queryClient.invalidateQueries(["rewardCodes"]);

        notify("Reward code updated successfully", { type: "success" });
      },
      onError: (error: Error) => {
        notify(`Error updating reward code: ${error.message}`, {
          type: "error",
        });
      },
    }
  );

  const deleteRewardMutation = useMutation(
    (ids: string[]) =>
      graphqlFetch({
        query:
          gql`
            mutation deleteRewardCode($id: uuid!) {
              delete_reward_codes_by_pk(id: $id) {
                id
              }
            }
          `.loc?.source?.body || "",
        variables: { id: ids[0] },
      }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["rewards"]);
        queryClient.invalidateQueries(["rewardCatalogues"]);

        notify("Reward deleted successfully", { type: "success" });
      },
      onError: (error: Error) => {
        notify(`Error deleting reward: ${error.message}`, { type: "error" });
      },
    }
  );

  return {
    exportRewardCode: exportRewardCode,
    generateReward: generateRewardCode,
    fetchRewardCodes: fetchRewardCodesQuery,
    updateReward: updateRewardMutation,
    deleteReward: deleteRewardMutation,

    // Helper functions for easy usage
    exportRewardCodeAsync: (variables: any) => {
      return exportRewardCode.mutateAsync(variables);
    },

    generateRewardCodeAsync: (rewardCodeData: any) => {
      return generateRewardCode.mutateAsync({ input: rewardCodeData });
    },

    fetchRewardCodesAsync: (variables: any) => {
      return fetchRewardCodesQuery.mutateAsync(variables);
    },

    updateRewardAsync: (id: string, updateData: any) => {
      return updateRewardMutation.mutateAsync({
        id: id,
        updateData: updateData,
      });
    },

    deleteRewardAsync: (id: string) => {
      return deleteRewardMutation.mutateAsync([id]);
    },
  };
};
